import { ShowDrawerFcn } from '@/components/common/DrawerManager';
import { FlexRow } from '@/components/common/Flex';
import { ArrowRight } from '@/components/common/icons';
import { Text } from '@/components/common/Typography';
import FinancialEntityBalance from '@/components/financialEntities/FinancialEntityBalance';
import { FinancialEntityIcon } from '@/components/financialEntities/FinancialEntityIcon';
import FinancialEntityWithMask from '@/components/financialEntities/FinancialEntityWithMask';
import { configByGoalType } from '@/components/goals/config';
import GoalIcon from '@/components/goals/GoalIcon';
import { showCompleteSetupOnMobileModal } from '@/components/goals/helpers';
import {
    Account,
    AccountListItemInfo,
    getAccountListItemInfo,
} from '@common/react/accounts';
import {
    getGoalSetupIncompleteLabel,
    isGoalSetupIncomplete,
} from '@common/react/goals';
import { UserInfo } from '@common/react/user';
import { toDollarsWithCents } from '@common/util/currency';

export interface AccountListItemProps
    extends Omit<AccountListItemInfo, 'subtitle'> {
    id: string;
    icon: React.ReactNode;
    subtitle: string | React.ReactNode;
    right: JSX.Element;
    onClick?: () => void;
}

const rightTextStyleProps = {
    fontSize: 'sm',
    fontWeight: 'medium',
};

const rightSubtitleStyleProps = {
    fontSize: 'sm',
    fontFamily: 'mono',
    color: 'gray',
};

export const getAccountListItemProps = ({
    userInfo,
    account,
    showDrawer,
}: {
    userInfo: UserInfo;
    account: Account;
    showDrawer: ShowDrawerFcn;
}): AccountListItemProps => {
    const info = getAccountListItemInfo({ userInfo, account });

    if (account.__typename === 'Goal') {
        return {
            ...info,
            id: account.id,
            icon: <GoalIcon goal={account} />,
            subtitle: info.subtitle,
            right: isGoalSetupIncomplete(account) ? (
                <FlexRow align="center" gap={2} color="blue.500">
                    <Text {...rightTextStyleProps}>
                        {getGoalSetupIncompleteLabel({
                            goal: account,
                            isMobile: false,
                        })}
                    </Text>
                    <ArrowRight size="sm" />
                </FlexRow>
            ) : (
                <Text {...rightTextStyleProps}>
                    {toDollarsWithCents(info.value ?? 0)}
                </Text>
            ),
            onClick: () => {
                if (
                    isGoalSetupIncomplete(account) &&
                    account.onboardingVersion !== 'v1'
                ) {
                    showCompleteSetupOnMobileModal();
                } else {
                    showDrawer({
                        drawerKey: 'goal-detail',
                        entityId: account.id,
                    });
                }
            },
        };
    }

    if (account.type === 'PlentyGoalAccount') {
        return {
            ...info,
            id: account.id,
            icon: (
                <FinancialEntityIcon
                    fe={account}
                    color={configByGoalType[account.goal.type].color}
                />
            ),
            subtitle: (
                <FinancialEntityWithMask
                    fe={account}
                    showName={false}
                    showOwnership={true}
                />
            ),
            right: (
                <FinancialEntityBalance
                    textProps={rightTextStyleProps}
                    subtitleProps={rightSubtitleStyleProps}
                    fe={account}
                />
            ),
            onClick: account.goalAccountOnboarding?.approvedAt
                ? () =>
                      showDrawer({
                          drawerKey: 'goal-account-detail',
                          entityId: account.id,
                      })
                : undefined,
        };
    }

    // Must be external FE
    return {
        ...info,
        id: account.id,
        icon: <FinancialEntityIcon fe={account} />,
        subtitle: (
            <FinancialEntityWithMask
                fe={account}
                showName={false}
                showOwnership={true}
            />
        ),
        right: (
            <FinancialEntityBalance
                textProps={rightTextStyleProps}
                subtitleProps={rightSubtitleStyleProps}
                fe={account}
            />
        ),
        onClick: () => {
            if (info.isLinkingInProgress) return;

            showDrawer({
                drawerKey: 'financial-entity-detail',
                entityId: account.id,
            });
        },
    };
};
